import Link from 'next/link';
import { useState } from 'react';

import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  delay,
  SectionHeading,
  tone,
} from '@/components/agents/primitives';
import { useReveal } from '@/components/agents/useReveal';

import { styleImageUrl } from './assets';
import { INTERIOR_STYLES } from './content';
import { useInteriorLandingT, usePageVars, useStartInterior } from './kit';
import type { InteriorLandingPage } from './types';

/** How many styles show before "Show all". */
const FIRST = 12;

/**
 * Every style in the designer. Picking one opens it with that style already
 * chosen (Restyle on photo pages, Create new without one).
 */
export default function StyleWall({ page }: { page: InteriorLandingPage }) {
  const { t, lang } = useInteriorLandingT();
  const vars = usePageVars(page);
  const start = useStartInterior(page);
  // The style guides are written in English only.
  const guides = lang === 'en';
  const reveal = useReveal<HTMLUListElement>(0.1);
  const [all, setAll] = useState(false);
  const shown = all ? INTERIOR_STYLES : INTERIOR_STYLES.slice(0, FIRST);

  return (
    <section id='styles' className='py-20 sm:py-28'>
      <div className='mx-auto max-w-7xl px-5 sm:px-6 lg:px-8'>
        <SectionHeading
          title={t('{{styles}} styles,', vars)}
          tail={t('one click away.')}
          lead={t(
            'Pick a style to open the designer with it chosen. Paid plans can compare up to 4 styles on the same photo at once.',
          )}
        />

        <ul
          ref={reveal.ref}
          data-reveal={reveal.state}
          className={clsxm(
            styles.reveal,
            'mt-12 grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-6',
          )}
        >
          {shown.map((style, index) => (
            <li
              key={style.slug}
              className={index < FIRST ? styles.rv : undefined}
              style={index < FIRST ? delay((index % 6) * 50) : undefined}
            >
              <button
                type='button'
                onClick={() =>
                  start({
                    surface: 'style',
                    mode: page.mode === 'restyle' ? 'restyle' : 'generate',
                    style: style.theme,
                  })
                }
                className='group relative block aspect-square w-full overflow-hidden rounded-[18px] text-start ring-1 ring-[#0f0f28]/[0.07] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 dark:ring-white/10'
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={styleImageUrl(style.slug)}
                  alt=''
                  width={360}
                  height={360}
                  loading='lazy'
                  decoding='async'
                  className='absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]'
                />
                <span className='absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent px-3 pb-2.5 pt-10 text-[14px] font-semibold text-white'>
                  {t(style.theme)}
                </span>
              </button>
            </li>
          ))}
        </ul>

        {all && !guides ? null : (
          <div className='mt-8 flex flex-wrap items-center gap-x-6 gap-y-3'>
            {all ? null : (
              <button
                type='button'
                onClick={() => setAll(true)}
                className={clsxm(
                  'rounded-full bg-white px-5 py-2.5 text-sm font-semibold ring-1 ring-[#0f0f28]/10 transition hover:ring-[#0f0f28]/25 dark:bg-white/[0.06] dark:ring-white/15',
                  tone.ink,
                )}
              >
                {t('Show all {{styles}} styles', vars)}
              </button>
            )}
            {guides ? (
              <Link
                href='/interior-design-styles'
                className='inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400'
              >
                Read our interior design style guides
                <ArrowGlyph />
              </Link>
            ) : null}
          </div>
        )}
      </div>
    </section>
  );
}
